import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DollarSign, Plus, MoreHorizontal, CheckCircle2, Banknote, Trash2, User, Percent, FileBarChart } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['all', 'pending', 'approved', 'paid'];

export default function CommissionsPage() {
  const { t } = useTranslation();
  const [commissions, setCommissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({ pending: { total: 0, count: 0 }, approved: { total: 0, count: 0 }, paid: { total: 0, count: 0 } });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ user_id: '', job_id: '', commission_type: 'percentage', percentage: 10, flat_amount: 0, notes: '' });

  // Payroll / per-worker report
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  const loadReport = async () => {
    setReportLoading(true);
    try {
      const params = {};
      if (reportFrom) params.date_from = reportFrom;
      if (reportTo) params.date_to = reportTo;
      const res = await api.get('/commissions/report', { params });
      setReportData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setReportLoading(false);
    }
  };

  const openReport = () => {
    setReportOpen(true);
    setReportData(null);
    loadReport();
  };

  const PAY_TYPE_LABELS = {
    commission: 'Comisión %', fixed: 'Monto fijo', per_well: 'Por pozo', per_job: 'Por trabajo', per_day: 'Por día', none: '—',
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [cRes, uRes, jRes, sRes] = await Promise.all([
        api.get('/commissions', { params }),
        api.get('/commissions/users'),
        api.get('/jobs', { params: { page_size: 500 } }),
        api.get('/commissions/summary')
      ]);
      setCommissions(cRes.data);
      setUsers(uRes.data);
      setJobs(jRes.data.items || jRes.data);
      setSummary(sRes.data);
    } catch { toast.error(t('commissions.errorLoading')); }
    finally { setLoading(false); }
  }, [statusFilter, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setForm({ user_id: '', job_id: '', commission_type: 'percentage', percentage: 10, flat_amount: 0, notes: '' });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.job_id) { toast.error(t('commissions.employeeJobRequired')); return; }
    try {
      await api.post('/commissions', form);
      toast.success(t('commissions.created'));
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('commissions.errorCreating')); }
  };

  const handleApprove = async (id) => {
    try { await api.post(`/commissions/${id}/approve`); toast.success(t('commissions.commissionApproved')); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || t('errors.generic')); }
  };

  const handlePay = async (id) => {
    try { await api.post(`/commissions/${id}/pay`); toast.success(t('commissions.commissionPaid')); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || t('errors.generic')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/commissions/${id}`); toast.success(t('commissions.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const getSelectedJobTotal = () => {
    const job = jobs.find(j => j.id === form.job_id);
    return job?.total || 0;
  };

  const calcPreview = () => {
    const base = getSelectedJobTotal();
    if (form.commission_type === 'percentage') return (base * form.percentage / 100).toFixed(2);
    return form.flat_amount.toFixed(2);
  };

  const statusLabelKey = (s) => {
    const map = { all: 'common.all', pending: 'commissions.pending', approved: 'commissions.approved', paid: 'commissions.paid' };
    return map[s] || s;
  };

  return (
    <div data-testid="commissions-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('commissions.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('commissions.subtitle')}</p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <Button data-testid="payroll-report-btn" onClick={openReport} size="sm" variant="outline" className="border-[#003366]/20 text-[#003366]">
            <FileBarChart className="w-4 h-4 mr-2" /> Reporte de Pagos
          </Button>
          <Button data-testid="new-commission-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm">
            <Plus className="w-4 h-4 mr-2" /> {t('commissions.newCommission')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {[
          { key: 'pending', label: t('commissions.pending'), color: 'text-amber-600', bg: 'bg-amber-50', icon: DollarSign, iconColor: 'text-amber-600' },
          { key: 'approved', label: t('commissions.approved'), color: 'text-blue-600', bg: 'bg-blue-50', icon: CheckCircle2, iconColor: 'text-blue-600' },
          { key: 'paid', label: t('commissions.paid'), color: 'text-emerald-600', bg: 'bg-emerald-50', icon: Banknote, iconColor: 'text-emerald-600' },
        ].map(({ key, label, color, bg, icon: Icon, iconColor }) => (
          <Card key={key} className="bg-white border-gray-100">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
                  <p className={`text-lg sm:text-2xl font-bold ${color} mt-1`}>${(summary[key]?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400">{summary[key]?.count || 0}</p>
                </div>
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${bg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="tabs-scroll">
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto">
          <TabsList className="bg-white border border-gray-200 h-9">
            {STATUSES.map(s => (
              <TabsTrigger key={s} value={s} data-testid={`comm-filter-${s}`} className="text-xs px-2.5 sm:px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                {t(statusLabelKey(s))}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : commissions.length === 0 ? (
            <div className="py-16 text-center">
              <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('commissions.noCommissions')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('commissions.createFirstCommission')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('commissions.employee')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('commissions.job')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('commissions.commissionType')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('commissions.base')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('commissions.commission')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('bookings.tableHeaders.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((c) => (
                    <tr key={c.id} data-testid={`commission-row-${c.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-[#003366]/10 flex items-center justify-center">
                            <User className="w-3.5 h-3.5 text-[#003366]" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{c.user_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-mono text-gray-500">{c.job_number}</p>
                        <p className="text-xs text-gray-400">{c.job_title}</p>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden md:table-cell">
                        {c.commission_type === 'percentage' ? (
                          <span className="flex items-center gap-1"><Percent className="w-3 h-3" />{c.percentage}%</span>
                        ) : t('commissions.flatAmount')}
                      </td>
                      <td className="px-5 py-3.5 text-right text-sm text-gray-600 hidden md:table-cell">${(c.base_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-5 py-3.5 text-right text-sm font-bold text-[#003366]">${(c.commission_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                      <td className="px-5 py-3.5 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {c.status === 'pending' && (
                              <DropdownMenuItem data-testid={`approve-commission-${c.id}`} onClick={() => handleApprove(c.id)}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('commissions.approveBtn')}
                              </DropdownMenuItem>
                            )}
                            {c.status === 'approved' && (
                              <DropdownMenuItem data-testid={`pay-commission-${c.id}`} onClick={() => handlePay(c.id)}>
                                <Banknote className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('commissions.payBtn')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-red-600">
                              <Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : commissions.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <DollarSign className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('commissions.noCommissions')}</p>
          </CardContent></Card>
        ) : (
          commissions.map((c, idx) => (
            <Card key={c.id} data-testid={`commission-mobile-${c.id}`} className="bg-white border-gray-100 shadow-sm mobile-card-enter" style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#003366]/10 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-[#003366]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{c.user_name}</p>
                      <p className="text-xs text-gray-400">{c.job_number} - {c.job_title}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {c.status === 'pending' && <DropdownMenuItem onClick={() => handleApprove(c.id)}><CheckCircle2 className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('commissions.approveBtn')}</DropdownMenuItem>}
                      {c.status === 'approved' && <DropdownMenuItem onClick={() => handlePay(c.id)}><Banknote className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('commissions.payBtn')}</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center justify-between">
                  <StatusBadge status={c.status} />
                  <span className="text-sm font-bold text-[#003366]">${(c.commission_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{t('commissions.newCommission')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('commissions.employee')} *</Label>
              <Select value={form.user_id} onValueChange={v => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="commission-user-select"><SelectValue placeholder={t('commissions.selectEmployee')} /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('commissions.job')} *</Label>
              <Select value={form.job_id} onValueChange={v => setForm({ ...form, job_id: v })}>
                <SelectTrigger data-testid="commission-job-select"><SelectValue placeholder={t('commissions.selectJob')} /></SelectTrigger>
                <SelectContent>
                  {jobs.map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.job_number} - {j.title} (${(j.total || 0).toLocaleString()})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('commissions.commissionType')}</Label>
              <Select value={form.commission_type} onValueChange={v => setForm({ ...form, commission_type: v })}>
                <SelectTrigger data-testid="commission-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">{t('commissions.percentage')}</SelectItem>
                  <SelectItem value="flat_rate">{t('commissions.flatAmount')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.commission_type === 'percentage' ? (
              <div className="space-y-2">
                <Label>{t('commissions.percentage')} (%)</Label>
                <Input data-testid="commission-percentage" type="number" value={form.percentage} onChange={e => setForm({ ...form, percentage: parseFloat(e.target.value) || 0 })} min="0" max="100" step="0.5" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{t('commissions.flatAmount')} ($)</Label>
                <Input data-testid="commission-flat-amount" type="number" value={form.flat_amount} onChange={e => setForm({ ...form, flat_amount: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('timecards.notes')}</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            {form.job_id && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium">{t('commissions.preview')}</p>
                <p className="text-sm text-gray-700">{t('commissions.base')}: <span className="font-semibold">${getSelectedJobTotal().toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></p>
                <p className="text-lg font-bold text-[#003366]">{t('commissions.commission')}: ${calcPreview()}</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-commission-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">{t('commissions.newCommission')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payroll / Per-worker Report */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="payroll-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-[#003366]" /> Reporte de Pagos por Persona
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row gap-3 items-end border-b pb-3">
            <div className="space-y-1">
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} className="h-9" data-testid="report-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} className="h-9" data-testid="report-to" />
            </div>
            <Button size="sm" onClick={loadReport} className="bg-[#003366] hover:bg-[#004080] text-white h-9" data-testid="report-apply">
              Aplicar
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto py-3">
            {reportLoading ? (
              <div className="py-12 text-center text-gray-400">{t('common.loading')}</div>
            ) : !reportData || reportData.report.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <FileBarChart className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">Sin datos</p>
                <p className="text-xs mt-1">Configura la remuneración de cada empleado en Settings → Users y asigna trabajos completados.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-[#003366]/5 rounded-lg px-4 py-2.5">
                  <span className="text-sm font-semibold text-gray-700">Total estimado del período</span>
                  <span className="text-lg font-bold text-[#003366]" data-testid="report-grand-total">${(reportData.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                {reportData.report.map(r => (
                  <Card key={r.user_id} className="border-gray-100" data-testid={`report-row-${r.user_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#003366]/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-[#003366]" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{r.user_name}</p>
                            <p className="text-[11px] text-gray-400">
                              {PAY_TYPE_LABELS[r.pay_type] || r.pay_type} · {r.pay_type === 'commission' ? `${r.pay_value}%` : `$${r.pay_value}`} · {r.job_count} trabajos
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-emerald-600">${(r.earned || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      {r.breakdown?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                          {r.breakdown.slice(0, 8).map((b, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] text-gray-500">
                              <span className="truncate">{b.job_number ? `${b.job_number} · ` : ''}{b.title}</span>
                              <span className="font-medium text-gray-700">${(b.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
